import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadManager } from "./UploadManager";
import { renewPath } from "../utils/renewTarget";
import "./UploadWidget.css";

function UploadWidget() {
  const uploadManager = useUploadManager();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!uploadManager) return null;
  const { uploads, pauseResume, cancelUpload, retryFailed, dismissUpload } = uploadManager;

  const entries = Object.entries(uploads)
    .filter(([, u]) => u && (u.isUploading || u.failedCount > 0 || u.success || u.error))
    .map(([key, u]) => ({ key, ...u }));

  if (entries.length === 0) return null;

  const activeCount = entries.filter((u) => u.isUploading).length;
  const overallProgress = Math.round(
    entries.reduce((sum, u) => sum + (u.progress || 0), 0) / entries.length
  );

  return (
    <div className="uw-widget">
      <div className="uw-header" onClick={() => setCollapsed((c) => !c)}>
        <span className="uw-header-icon">☁️</span>
        <span className="uw-header-title">
          {activeCount > 0
            ? `Uploading ${activeCount} folder${activeCount > 1 ? "s" : ""}...`
            : "Uploads"}
        </span>
        {activeCount > 0 && (
          <span className="uw-header-progress">{overallProgress}%</span>
        )}
        <button className="uw-collapse-btn" title={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? "▲" : "▼"}
        </button>
      </div>

      {!collapsed && (
        <div className="uw-list">
          {entries.map((u) => {
            const label = u.eventName || `Event #${u.eventId}`;
            const fullLabel = u.folderLabel ? `${label} · ${u.folderLabel}` : label;
            return (
            <div key={u.key} className="uw-item">
              <div className="uw-item-top" onClick={() => navigate(`/events/${u.eventId}`)}>
                <span className="uw-item-name" title={fullLabel}>
                  {fullLabel}
                </span>
                {!u.isUploading && (
                  <button
                    className="uw-dismiss-btn"
                    title="Dismiss"
                    onClick={(e) => { e.stopPropagation(); dismissUpload(u.eventId, u.folderId); }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {u.isUploading ? (
                <>
                  <div className="uw-progress-bar">
                    <div className="uw-progress-fill" style={{ width: `${u.progress || 0}%` }} />
                  </div>
                  <div className="uw-item-row">
                    <span className="uw-item-status">
                      {u.progress || 0}% {u.statusText ? `— ${u.statusText}` : ""}
                    </span>
                    <div className="uw-item-actions">
                      <button
                        className="uw-mini-btn uw-pause"
                        onClick={(e) => { e.stopPropagation(); pauseResume(u.eventId, u.folderId); }}
                      >
                        {u.isPaused ? "Resume" : "Pause"}
                      </button>
                      <button
                        className="uw-mini-btn uw-cancel"
                        onClick={(e) => { e.stopPropagation(); cancelUpload(u.eventId, u.folderId); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="uw-item-row">
                  <span className={`uw-item-status ${u.failedCount > 0 ? "uw-status-error" : "uw-status-ok"}`}>
                    {u.failedCount > 0 ? `⚠️ ${u.failedCount} failed` : u.success ? `✅ ${u.success}` : u.error}
                  </span>
                  {u.subscriptionPaused ? (
                    <button
                      className="uw-mini-btn uw-recharge"
                      onClick={async (e) => { e.stopPropagation(); navigate(await renewPath()); }}
                    >
                      Renew Plan
                    </button>
                  ) : u.failedCount > 0 && u.lastFinishedJobId && (
                    <button
                      className="uw-mini-btn uw-retry"
                      onClick={(e) => { e.stopPropagation(); retryFailed(u.eventId, u.folderId); }}
                    >
                      🔄 Retry
                    </button>
                  )}
                </div>
              )}

              {/* RAW originals keep uploading after the gallery is ready; this is
                  the only cue that closing the tab now would abandon a backup. */}
              {u.rawBackupText && (
                <div className="uw-item-row uw-raw-backup">
                  <span className="uw-item-status">💾 {u.rawBackupText}</span>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UploadWidget;
